import { baseApi } from "../baseApi";
import { pick } from "../util";

export interface EmailConfig {
  id: string;
  name: string;
  provider: string;
  fromEmail: string;
  fromName?: string | null;
  replyTo?: string[] | null;
  isActive: boolean;
  apiKeyLast4?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailConfigInput {
  name: string;
  provider?: string;
  apiKey: string;
  fromEmail: string;
  fromName?: string;
  replyTo?: string[];
  isActive?: boolean;
}

export interface UpdateEmailConfigInput {
  name?: string;
  provider?: string;
  apiKey?: string;
  fromEmail?: string;
  fromName?: string;
  replyTo?: string[];
  isActive?: boolean;
}

const EMAIL_CONFIG_FIELDS = `
  id
  name
  provider
  fromEmail
  fromName
  replyTo
  isActive
  apiKeyLast4
  createdAt
  updatedAt
`;

export const emailConfigApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEmailConfigs: builder.query<EmailConfig[], void>({
      query: () => ({
        body: {
          query: `query EmailConfigs { emailConfigs { ${EMAIL_CONFIG_FIELDS} } }`,
        },
      }),
      transformResponse: (res: unknown) =>
        pick<EmailConfig[]>(res, "emailConfigs"),
      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({ type: "EmailConfig" as const, id: c.id })),
              { type: "EmailConfig" as const, id: "LIST" },
            ]
          : [{ type: "EmailConfig" as const, id: "LIST" }],
    }),

    createEmailConfig: builder.mutation<EmailConfig, CreateEmailConfigInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateEmailConfig($input: CreateEmailConfigInput!) {
              createEmailConfig(input: $input) { ${EMAIL_CONFIG_FIELDS} }
            }
          `,
          variables: { input },
        },
      }),
      transformResponse: (res: unknown) =>
        pick<EmailConfig>(res, "createEmailConfig"),
      invalidatesTags: [{ type: "EmailConfig", id: "LIST" }],
    }),

    updateEmailConfig: builder.mutation<
      EmailConfig,
      { id: string; input: UpdateEmailConfigInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateEmailConfig($id: String!, $input: UpdateEmailConfigInput!) {
              updateEmailConfig(id: $id, input: $input) { ${EMAIL_CONFIG_FIELDS} }
            }
          `,
          variables: { id, input },
        },
      }),
      transformResponse: (res: unknown) =>
        pick<EmailConfig>(res, "updateEmailConfig"),
      invalidatesTags: [{ type: "EmailConfig", id: "LIST" }],
    }),

    deleteEmailConfig: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `mutation DeleteEmailConfig($id: String!) { deleteEmailConfig(id: $id) }`,
          variables: { id },
        },
      }),
      transformResponse: (res: unknown) =>
        pick<boolean>(res, "deleteEmailConfig"),
      invalidatesTags: [{ type: "EmailConfig", id: "LIST" }],
    }),

    setActiveEmailConfig: builder.mutation<EmailConfig, string>({
      query: (id) => ({
        body: {
          query: `
            mutation SetActiveEmailConfig($id: String!) {
              setActiveEmailConfig(id: $id) { ${EMAIL_CONFIG_FIELDS} }
            }
          `,
          variables: { id },
        },
      }),
      transformResponse: (res: unknown) =>
        pick<EmailConfig>(res, "setActiveEmailConfig"),
      invalidatesTags: [{ type: "EmailConfig", id: "LIST" }],
    }),

    sendTestEmail: builder.mutation<boolean, { id: string; to: string }>({
      query: ({ id, to }) => ({
        body: {
          query: `mutation SendTestEmail($id: String!, $to: String!) { sendTestEmail(id: $id, to: $to) }`,
          variables: { id, to },
        },
      }),
      transformResponse: (res: unknown) => pick<boolean>(res, "sendTestEmail"),
    }),
  }),
});

export const {
  useGetEmailConfigsQuery,
  useCreateEmailConfigMutation,
  useUpdateEmailConfigMutation,
  useDeleteEmailConfigMutation,
  useSetActiveEmailConfigMutation,
  useSendTestEmailMutation,
} = emailConfigApi;
